/**
 * hudSnapshot.ts
 * Read-only HUD data snapshot for src/game/ui/ components.
 *
 * ARCHITECTURE:
 *   This module lives in src/game/engine/ (engine layer) and provides a
 *   dedicated read-only boundary for HUD values:
 *   - physicsStep writes the snapshot each tick (engine side)
 *   - HUD.tsx reads from the snapshot via polling (UI side)
 *
 *   This keeps dependency direction correct (engine → UI reads engine data),
 *   rather than engine importing from rendering layer.
 *
 *   AGENTS.md specifies src/game/ui/ should read via SharedValue. This module
 *   uses a plain JS object because physicsStep.ts cannot import Reanimated
 *   (shared by web build). Phase 3 can introduce SharedValues by having a
 *   separate sync layer between this snapshot and Reanimated primitives.
 *
 * Phase 2 — Combat, Enemies, Checkpoints, HUD.
 *
 * @module hudSnapshot
 */

import { NOVA_MAX_HP, NOVA_LIVES_START } from '../../constants/gameConstants';
import type { ProjectileKind } from '../entities/Projectile';

/** Shape of the HUD data snapshot. */
export interface HudSnapshot {
  readonly health: number;
  readonly maxHealth: number;
  readonly seeds: number;
  readonly shardsCollected: number;
  readonly shardsTotal: number;
  readonly score: number;
  readonly lives: number;
  readonly elapsedSeconds: number;
  readonly projectileKind: ProjectileKind;
  readonly ammo: number;
}

/**
 * Module-scoped mutable snapshot object.
 * DECISION: Mutate fields in-place rather than replacing the object each tick
 * to avoid allocating a new object at 60fps (reduces GC churn).
 * @internal
 */
const snapshot: {
  health: number;
  maxHealth: number;
  seeds: number;
  shardsCollected: number;
  shardsTotal: number;
  score: number;
  lives: number;
  elapsedSeconds: number;
  projectileKind: ProjectileKind;
  ammo: number;
} = {
  health: NOVA_MAX_HP,
  maxHealth: NOVA_MAX_HP,
  seeds: 0,
  shardsCollected: 0,
  shardsTotal: 0,
  score: 0,
  lives: NOVA_LIVES_START,
  elapsedSeconds: 0,
  projectileKind: 'star_shard',
  ammo: 0,
};

/**
 * Update the HUD snapshot in place. Called from physicsStep each tick.
 * Mutates fields of the module-scoped object to avoid per-tick allocations.
 *
 * @param health         - Current health.
 * @param maxHealth      - Maximum health.
 * @param seeds          - Star Seeds collected.
 * @param shardsCollected - Constellation Shards collected.
 * @param shardsTotal     - Total shards in the level.
 * @param score          - Current score.
 * @param lives          - Remaining lives.
 * @param elapsedSeconds - Elapsed time in seconds.
 * @param projectileKind - Active weapon kind.
 * @param ammo           - Remaining ammo for the active weapon (0 for star_shard).
 */
export function updateHudSnapshot(
  health: number,
  maxHealth: number,
  seeds: number,
  shardsCollected: number,
  shardsTotal: number,
  score: number,
  lives: number,
  elapsedSeconds: number,
  projectileKind: ProjectileKind,
  ammo: number,
): void {
  snapshot.health = health;
  snapshot.maxHealth = maxHealth;
  snapshot.seeds = seeds;
  snapshot.shardsCollected = shardsCollected;
  snapshot.shardsTotal = shardsTotal;
  snapshot.score = score;
  snapshot.lives = lives;
  snapshot.elapsedSeconds = elapsedSeconds;
  snapshot.projectileKind = projectileKind;
  snapshot.ammo = ammo;
}

/**
 * Get the current HUD snapshot for UI rendering.
 * Returns the mutable module-scoped object typed as readonly.
 * WARNING: Do not retain references long-term — fields are mutated each tick.
 *
 * @returns Current HUD values snapshot (typed readonly but mutated in place).
 */
export function getHudSnapshot(): Readonly<HudSnapshot> {
  return snapshot;
}

/**
 * Reset the HUD snapshot to initial values.
 * Call from PlayScene.init() to clear stale data from previous session.
 */
export function resetHudSnapshot(): void {
  snapshot.health = NOVA_MAX_HP;
  snapshot.maxHealth = NOVA_MAX_HP;
  snapshot.seeds = 0;
  snapshot.shardsCollected = 0;
  snapshot.shardsTotal = 0;
  snapshot.score = 0;
  snapshot.lives = NOVA_LIVES_START;
  snapshot.elapsedSeconds = 0;
  snapshot.projectileKind = 'star_shard';
  snapshot.ammo = 0;
}
